import React, { useState, useEffect } from "react"

import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

const KeywordsEditor = ({ section, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "I help with",
    subheadline: section?.subheadline || "Specialized care for your needs",
    body: section?.body || "",
    settings: {
      maxKeywords: 1000,
      allowClick: true,
      keywords: [],
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      ...section?.settings
    }
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "I help with",
        subheadline: section.subheadline || "Specialized care for your needs",
        body: section.body || "",
        settings: {
          maxKeywords: 1000,
          allowClick: true,
          keywords: [],
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          ...section.settings
        }
      })
    }
  }, [section])

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // If updating showBackgroundColor, also update backgroundColor atomically
      if (key === "showBackgroundColor") {
        newSettings.backgroundColor = value ? "theme" : "#ffffff"
      }

      const newFormData = {
        ...prev,
        settings: newSettings
      }

      // Update preview immediately for background color changes
      // Preserve all section data including keywords, headline, etc.
      if (section?.id && (key === "showBackgroundColor" || key === "backgroundColor")) {
        // Ensure keywords are preserved - if section.settings has keywords, keep them
        // Otherwise, if newFormData.settings has keywords, use those
        // If neither has keywords, the component will fall back to getKeywordsFromPractice()
        const preservedSettings = {
          ...section.settings, // Preserve existing settings from section (including keywords if present)
          ...newFormData.settings, // Merge with new settings (background color changes)
          // Explicitly preserve keywords if they exist in section.settings
          keywords: section.settings?.keywords || newFormData.settings?.keywords
        }

        handleUpdateSectionPreview(section.id, {
          ...section,
          headline: newFormData.headline,
          subheadline: newFormData.subheadline,
          body: newFormData.body,
          settings: preservedSettings
        })
      }

      return newFormData
    })
  }

  const handleSubmit = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    onSave({
      layout: "keywords",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  return (
    <div
      className="relative box-border flex size-full flex-col content-stretch items-center justify-start bg-white shadow-[0px_8px_24px_0px_rgba(33,37,41,0.12)]"
      data-name="Keywords"
      data-node-id="1025:23393">
      {/* Form Content */}
      <div
        className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pb-[120px] pt-6"
        data-name="form"
        data-node-id="1025:23395">
        <div
          className="relative flex w-[432px] shrink-0 flex-col content-stretch items-start justify-start gap-4"
          data-name="Builder / Keywords"
          data-node-id="1025:23396">
          <div
            className="relative flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 leading-[0] text-[#0b3954]"
            id="node-I1025_23396-984_20635">
            <div
              className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold"
              id="node-I1025_23396-984_20636">
              <p className="leading-[24px]">Keywords</p>
            </div>
            <div
              className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal"
              id="node-I1025_23396-984_20637">
              <p className="leading-[20px]">
                <span className>{`Manage your keywords from the `}</span>
                <span className="text-[#0d9da4]">{`My Practice > Keywords`}</span>
                <span className>{` section.`}</span>
              </p>
            </div>
          </div>

          {/* Background Color Toggle */}
          <BackgroundColorToggle formData={formData} onSettingsChange={handleSettingsChange} sectionType="keywords" />
        </div>
      </div>

      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  )
}

export default KeywordsEditor
